import React, { useState } from 'react';
import { StarIcon } from "../../assets/image/StarIcon";
import { WhiteStarIcon } from "../../assets/image/WhiteStarIcon";
import { validateField } from "../../utils/helperFunctions";
import { useTranslation } from "react-i18next";
import styles from './TestimonialsForm.module.css';

export function TestimonialsForm({ onSubmit, handleCloseTestimonialsModal, isPopup = false }) {
    const { t } = useTranslation();

    const [recommendValue, setRecommendValue] = useState(0);
    const [recommendHover, setRecommendHover] = useState(0);
    const [navigationValue, setNavigationValue] = useState(0);
    const [navigationHover, setNavigationHover] = useState(0);
    const [overallValue, setOverallValue] = useState(0);
    const [overallHover, setOverallHover] = useState(0);
    const [seeService, setSeeService] = useState('');
    const [otherService, setOtherService] = useState('');
    const [userReview, setUserReview] = useState('');
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({
        seeService: '',
        otherService: '',
        userReview: ''
    });

    const handleServiceChange = (e) => {
        const { value } = e.target;
        const error = validateField('seeService', value, t);
        setErrors(prev => ({ ...prev, seeService: error }));
        setSeeService(value);
    };

    const handleOtherServiceChange = (e) => {
        const { value } = e.target;
        const error = validateField('otherService', value, t);
        setErrors(prev => ({ ...prev, otherService: error }));
        setOtherService(value);
    };

    const handleReviewChange = (e) => {
        const { value } = e.target;
        const error = validateField('userReview', value, t);
        setErrors(prev => ({ ...prev, userReview: error }));
        setUserReview(value);
    };

    const handleReset = () => {
        setRecommendValue(0);
        setRecommendHover(0);
        setNavigationValue(0);
        setNavigationHover(0);
        setOverallValue(0);
        setOverallHover(0);
        setSeeService('');
        setOtherService('');
        setUserReview('');
        setErrors({
            seeService: '',
            otherService: '',
            userReview: ''
        });
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        setButtonDisabled(true);

        const newErrors = {
            seeService: validateField('seeService', seeService, t),
            otherService: seeService === 'Other'
                ? validateField('otherService', otherService, t)
                : '',
            userReview: validateField('userReview', userReview, t)
        };

        setErrors(newErrors);

        if (Object.values(newErrors).some(error => error !== '')) {
            setButtonDisabled(false);
            return;
        }

        const formData = new FormData();
        formData.append("recommendValue", Number(recommendValue));
        formData.append("navigationValue", Number(navigationValue));
        formData.append("overallValue", Number(overallValue));
        formData.append("seeService", seeService);
        formData.append("userReview", userReview);

        if (seeService === 'Other') {
            formData.append("otherService", otherService);
        }

        const language = localStorage.getItem("language");
        if (language) {
            formData.append("language", language);
        }


        try {
            await onSubmit(formData);
            handleCloseTestimonialsModal();
            handleReset();
        } catch (error) {
            console.error("Error submitting testimonial form", error);
        } finally {
            setButtonDisabled(false);
        }
    };

    return (
        <div className={`${styles.surveyContainer} ${isPopup ? styles.popupForm : ''}`}>
            {!isPopup && (
                <>
                    <h2 className={styles.testimonialsHeading}>{t('testimonials.testimonial-title')}</h2>
                    <p className={styles.surveySubtitle}>
                        {t('testimonials.testimonial-text')}
                    </p>
                </>
            )}

            <div className={styles.surveySection}>
                {!isPopup && <div className={styles.sectionDivider}></div>}
                {!isPopup && <h2 className={styles.sectionTitle}>{t('testimonials.about-your-experience')}</h2>}

                <div className={styles.ratingQuestion}>
                    <p className={styles.text}>{t('testimonials.would-you-recommend')}</p>
                    <div style={{ display: 'flex', gap: 4, cursor: 'pointer' }}>
                        {[1, 2, 3, 4, 5].map((starIndex) => (
                            <span
                                key={starIndex}
                                onClick={() => setRecommendValue(starIndex)}
                                onMouseEnter={() => setRecommendHover(starIndex)}
                                onMouseLeave={() => setRecommendHover(0)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setRecommendValue(starIndex);
                                    }
                                }}
                            >
                                {starIndex <= (recommendHover || recommendValue) ? (
                                    <StarIcon width={12} height={12} fill="#A98D4B" />
                                ) : (
                                    <WhiteStarIcon width={12} height={12} />
                                )}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Navigation Rating */}
                <div className={styles.ratingQuestion}>
                    <p className={styles.text}>{t('testimonials.rate-site-navigation')}</p>
                    <div style={{ display: 'flex', gap: 4, cursor: 'pointer' }}>
                        {[1, 2, 3, 4, 5].map((starIndex) => (
                            <span
                                key={starIndex}
                                onClick={() => setNavigationValue(starIndex)}
                                onMouseEnter={() => setNavigationHover(starIndex)}
                                onMouseLeave={() => setNavigationHover(0)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setNavigationValue(starIndex);
                                    }
                                }}
                            >
                                {starIndex <= (navigationHover || navigationValue) ? (
                                    <StarIcon width={12} height={12} fill="#A98D4B" />
                                ) : (
                                    <WhiteStarIcon width={12} height={12} />
                                )}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Additional Services */}
                <div className={styles.surveySection}>
                    <p className={styles.text}>{t('testimonials.additional-services')}</p>
                    <div className={styles.radioOptions}>
                        <label>
                            <input
                                type="radio"
                                name="seeService"
                                value="Cleaning"
                                checked={seeService === 'Cleaning'}
                                onChange={handleServiceChange}
                            />
                            {t('testimonials.Cleaning')}
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="seeService"
                                value="Elderly Care"
                                checked={seeService === 'Elderly Care'}
                                onChange={handleServiceChange}
                            />
                            {t('testimonials.Elderly-care')}
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="seeService"
                                value="Pet Care"
                                checked={seeService === 'Pet Care'}
                                onChange={handleServiceChange}
                            />
                            {t('testimonials.Pet-care')}
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="seeService"
                                value="Other"
                                checked={seeService === 'Other'}
                                onChange={handleServiceChange}
                            />
                            {t('testimonials.Other')}
                        </label>
                    </div>
                    {errors.seeService && <div className={styles.errorfield}>{errors.seeService}</div>}

                    {seeService === 'Other' && (
                        <div className={styles.textInput}>
                            <input
                                type="text"
                                placeholder={t('testimonials.type-here')}
                                value={otherService}
                                onChange={handleOtherServiceChange}
                                maxLength={70}
                                className={errors.otherService && styles.bordererror}
                            />
                            <div className={styles.characterCount}>{t('testimonials.characters-num')} {70 - otherService.length}</div>
                            {errors.otherService && <div className={styles.errorfield}>{errors.otherService}</div>}
                        </div>
                    )}
                </div>

                {/* Overall Rating */}
                {!isPopup && <div className={styles.sectionDividerSmall}></div>}
                <div className={styles.ratingQuestion}>
                    <p className={styles.text}>{t('testimonials.rate-impression')}</p>
                    <div style={{ display: 'flex', gap: 4, cursor: 'pointer' }}>
                        {[1, 2, 3, 4, 5].map((starIndex) => (
                            <span
                                key={starIndex}
                                onClick={() => setOverallValue(starIndex)}
                                onMouseEnter={() => setOverallHover(starIndex)}
                                onMouseLeave={() => setOverallHover(0)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setOverallValue(starIndex);
                                    }
                                }}
                            >
                                {starIndex <= (overallHover || overallValue) ? (
                                    <StarIcon width={12} height={12} fill="#A98D4B" />
                                ) : (
                                    <WhiteStarIcon width={12} height={12} />
                                )}
                            </span>
                        ))}
                    </div>
                </div>

                {/* User Review */}
                <div className={styles.textInput}>
                    <p className={styles.text}>{t('testimonials.write-your-review')}</p>
                    <input
                        type="text"
                        placeholder={t('testimonials.type-here')}
                        value={userReview}
                        onChange={handleReviewChange}
                        maxLength={70}
                        className={errors.userReview ? styles.bordererror : ''}
                    />
                    <div className={styles.characterCount}>{t('testimonials.characters-num')} {70 - userReview.length}</div>
                    {errors.userReview && <div className={styles.errorfield}>{errors.userReview}</div>}
                </div>

                {/* Form Buttons */}
                <div className={styles.surveyButtons}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleCloseTestimonialsModal}
                    >
                        {t('testimonials.btnCancel')}
                    </button>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={buttonDisabled}
                    >
                        {t('testimonials.btnSubmit')}
                    </button>
                </div>
            </div>
        </div>
    );
}